'use client'

import { usePrivy } from '@privy-io/react-auth'
import { useMemo } from 'react'
import { Address } from 'viem'
import { useAccount } from 'wagmi'

import { getChainConfigEntry } from '@/config/chain'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Objective } from '@/features/actions/logic/types'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Slider } from '@/ui/atoms/slider/Slider'

import { pooledBorrowDialogConfig } from '../dialogs/PooledBorrowDialog'

interface PooledBorrowPanelProps {
  collateral: Address
  liability: Address
  collateralSymbol?: string
  liabilitySymbol?: string
  borrowAPY?: number
  maxLTV?: bigint
  /** Liquidation LTV in basis points (from route info) */
  liquidationLTV?: bigint
  /** Controlled collateral amount from parent */
  collateralAmount?: string
  /** Controlled borrow amount from parent */
  borrowAmount?: string
  /** Callback to update borrow amount in parent */
  onBorrowAmountChange?: (value: string) => void
  /** Collateral price in USD */
  collateralPriceUsd?: number
  /** Borrow asset price in USD */
  borrowPriceUsd?: number
  /** Whether to use native HYPE instead of WHYPE for collateral */
  useNativeCollateral?: boolean
}

export function PooledBorrowPanel({
  collateral,
  liability,
  collateralSymbol: _collateralSymbol = 'Unknown',
  liabilitySymbol: _liabilitySymbol = 'Unknown',
  borrowAPY,
  maxLTV,
  liquidationLTV,
  collateralAmount = '',
  borrowAmount = '',
  onBorrowAmountChange,
  collateralPriceUsd = 0,
  borrowPriceUsd = 0,
  useNativeCollateral = false,
}: PooledBorrowPanelProps) {
  // Note: _collateralSymbol and _liabilitySymbol are available for future use in UI
  void _collateralSymbol
  void _liabilitySymbol
  const { address: userAddress } = useAccount()
  const { connectWallet } = usePrivy()
  const { chainId } = usePageChainId()
  const { marketInfo } = useMarketInfo({ chainId })
  const openDialog = useOpenDialog()

  // Get native asset info from chain config
  const chainConfig = getChainConfigEntry(chainId)
  const nativeAssetInfo = chainConfig.markets?.nativeAssetInfo

  // Find the reserve for the borrow asset
  const borrowReserve = useMemo(() => {
    return marketInfo.reserves.find((r) => r.token.address.toLowerCase() === liability.toLowerCase())
  }, [marketInfo.reserves, liability])

  // Find the reserve for the collateral asset (wrapped token like WHYPE)
  const collateralReserve = useMemo(() => {
    return marketInfo.reserves.find((r) => r.token.address.toLowerCase() === collateral.toLowerCase())
  }, [marketInfo.reserves, collateral])

  // Find the native reserve (e.g., HYPE) if using native collateral
  const nativeReserve = useMemo(() => {
    if (!useNativeCollateral || !nativeAssetInfo) return undefined
    return marketInfo.findReserveBySymbol(nativeAssetInfo.nativeAssetSymbol)
  }, [marketInfo, useNativeCollateral, nativeAssetInfo])

  // Get LTV values from props (route info) or fallback to reserve data
  const maxLTVPercent = maxLTV ? Number(maxLTV) / 100 : collateralReserve ? Number(collateralReserve.maxLtv) * 100 : 80

  // Use liquidationLTV from props if available, otherwise from reserve
  const liquidationLTVPercent = liquidationLTV
    ? Number(liquidationLTV) / 100
    : collateralReserve
      ? Number(collateralReserve.liquidationThreshold) * 100
      : 85

  // Get existing position from marketInfo (unified pooled position)
  const existingCollateralUsd = Number(marketInfo.userPositionSummary.totalCollateralUSD)
  const existingDebtUsd = Number(marketInfo.userPositionSummary.totalBorrowsUSD)
  const hasExistingPosition = existingCollateralUsd > 0 || existingDebtUsd > 0
  void hasExistingPosition // Used to determine if user can borrow without new collateral

  // Calculate NEW values being added
  const newCollateralValueUsd =
    collateralAmount && Number(collateralAmount) > 0 ? Number(collateralAmount) * collateralPriceUsd : 0
  const newBorrowValueUsd = borrowAmount && Number(borrowAmount) > 0 ? Number(borrowAmount) * borrowPriceUsd : 0

  // Calculate TOTAL position (existing + new)
  const totalCollateralValueUsd = existingCollateralUsd + newCollateralValueUsd
  const totalDebtUsd = existingDebtUsd + newBorrowValueUsd

  // Final LTV based on TOTAL position
  const finalLtvPercent = totalCollateralValueUsd > 0 ? (totalDebtUsd / totalCollateralValueUsd) * 100 : 0

  // Health score (liquidation LTV / final LTV)
  const healthScore = finalLtvPercent > 0 ? liquidationLTVPercent / finalLtvPercent : Number.POSITIVE_INFINITY

  // Calculate max additional borrow based on total collateral
  const maxBorrowableUsd = (totalCollateralValueUsd * maxLTVPercent) / 100 - existingDebtUsd
  const _maxNewBorrowAmount = borrowPriceUsd > 0 ? Math.max(0, maxBorrowableUsd) / borrowPriceUsd : 0
  void _maxNewBorrowAmount // Available for future use (e.g., max button)

  // Determine which token to use for collateral deposit
  // If useNativeCollateral is true and we have a native reserve, use the native token (HYPE)
  // Otherwise use the wrapped token (WHYPE)
  const depositToken = useNativeCollateral && nativeReserve ? nativeReserve.token : collateralReserve?.token

  // Create objectives for deposit + borrow
  const objectives: Objective[] = useMemo(() => {
    const objs: Objective[] = []

    // Add deposit objective if collateral amount is provided
    if (collateralAmount && depositToken && Number(collateralAmount) > 0) {
      objs.push({
        type: 'deposit',
        token: depositToken,
        value: NormalizedUnitNumber(collateralAmount),
      })
    }

    // Add borrow objective if borrow amount is provided
    if (borrowAmount && borrowReserve && Number(borrowAmount) > 0) {
      objs.push({
        type: 'borrow',
        token: borrowReserve.token,
        value: NormalizedUnitNumber(borrowAmount),
      })
    }

    return objs
  }, [collateralAmount, borrowAmount, depositToken, borrowReserve])

  // Handle slider change - calculates new borrow based on total position
  const handleSliderChange = (values: number[]) => {
    const targetLtvPercent = values[0] || 0
    if (totalCollateralValueUsd <= 0 || borrowPriceUsd <= 0) return

    // Calculate target total debt for the desired LTV
    const targetTotalDebtUsd = (targetLtvPercent * totalCollateralValueUsd) / 100
    // New borrow = target total debt - existing debt
    const newBorrowUsd = Math.max(0, targetTotalDebtUsd - existingDebtUsd)
    const newBorrowAmount = newBorrowUsd / borrowPriceUsd

    if (onBorrowAmountChange) {
      onBorrowAmountChange(newBorrowAmount > 0 ? newBorrowAmount.toFixed(6) : '')
    }
  }

  // Can proceed if: (has collateral to deposit) OR (has borrow amount with sufficient collateral)
  const hasCollateralToDeposit = collateralAmount && Number(collateralAmount) > 0
  const hasBorrowAmount = borrowAmount && Number(borrowAmount) > 0
  const canProceed = Boolean(
    (hasCollateralToDeposit || (hasBorrowAmount && totalCollateralValueUsd > 0)) &&
      finalLtvPercent <= maxLTVPercent,
  )

  // Determine button text
  const getButtonText = () => {
    if (!userAddress) return 'Connect Wallet'
    // Need either collateral to deposit or existing collateral to borrow against
    if (!hasCollateralToDeposit && totalCollateralValueUsd <= 0) return 'Enter Collateral Amount'
    if (finalLtvPercent > maxLTVPercent) return 'Exceeds Max LTV'
    // Show appropriate action based on what user is doing
    if (hasCollateralToDeposit && !hasBorrowAmount) return 'Deposit Collateral'
    if (!hasCollateralToDeposit && hasBorrowAmount) return 'Borrow'
    return 'Continue'
  }

  // Handle opening the dialog
  const handleContinue = () => {
    if (!depositToken || !borrowReserve) return

    openDialog(pooledBorrowDialogConfig, {
      collateralToken: depositToken,
      collateralAmount: NormalizedUnitNumber(collateralAmount || '0'),
      borrowToken: borrowReserve.token,
      borrowAmount: NormalizedUnitNumber(borrowAmount || '0'),
      objectives,
    })
  }

  return (
    <Panel.Wrapper className="flex flex-col gap-4 p-6">
      {/* LTV Display */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-base">Loan to Value (LTV)</span>
          <span
            className={`font-semibold text-base ${
              finalLtvPercent > liquidationLTVPercent * 0.9
                ? 'text-red-400'
                : finalLtvPercent > liquidationLTVPercent * 0.7
                  ? 'text-amber-400'
                  : 'text-emerald-400'
            }`}
          >
            {finalLtvPercent.toFixed(2)}%
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm">Max LTV</span>
          <span className="text-muted-foreground text-sm">max {maxLTVPercent.toFixed(2)}%</span>
        </div>
      </div>

      {/* LTV Slider */}
      <Slider
        className="mt-4"
        value={[Math.min(finalLtvPercent, maxLTVPercent)]}
        max={maxLTVPercent}
        step={0.1}
        onValueChange={handleSliderChange}
        disabled={!userAddress || totalCollateralValueUsd <= 0}
        trackClassName="bg-gradient-to-r from-emerald-500 via-amber-500 via-70% to-red-500"
        fixedGradient
      />
      <div className="flex items-center justify-between text-white/30 text-xs">
        <span>0%</span>
        <span>
          Max: {maxLTVPercent.toFixed(1)}% · Liq: {liquidationLTVPercent.toFixed(1)}%
        </span>
      </div>

      {/* Button */}
      {!userAddress ? (
        <Button type="button" onClick={connectWallet} variant="primary" className="mt-4 w-full" rounded="full">
          Connect Wallet
        </Button>
      ) : (
        <Button
          type="button"
          onClick={handleContinue}
          disabled={!canProceed}
          variant="primary"
          className="mt-4 w-full"
          rounded="full"
        >
          {getButtonText()}
        </Button>
      )}

      {/* Info block below button */}
      <div className="mt-6 flex flex-col gap-2 rounded-xl border bg-card p-4">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm tracking-[-0.3px]">Borrow APY</span>
          <span className="text-muted-foreground text-sm tracking-[-0.3px]">
            {borrowAPY !== undefined ? `${borrowAPY.toFixed(2)}%` : '-'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm tracking-[-0.3px]">Liquidation LTV</span>
          <span className="text-muted-foreground text-sm tracking-[-0.3px]">{liquidationLTVPercent.toFixed(2)}%</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-sm tracking-[-0.3px]">Health Score</span>
          <span className="text-muted-foreground text-sm tracking-[-0.3px]">
            {healthScore > 100 ? '>100' : healthScore === Number.POSITIVE_INFINITY ? '∞' : healthScore.toFixed(2)}
          </span>
        </div>
      </div>
    </Panel.Wrapper>
  )
}
